import type { ComponentType, ReactNode } from 'react';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
  MULTIPLAYER_APPROVAL_TOOL_NAMES,
  readMultiplayerApprovalPreview,
} from '../../data/projects/components/ApprovalRequestBlock.multiplayer';
import type { ApprovalRequestInput } from '../projects/components/ApprovalRequestBlock.display';
import {
  MERGE_PATIENT_PROFILES_TOOL_NAME,
  MERGE_SESSIONS_TOOL_NAME,
  readMergeApprovalFields,
  readMergeSessionsApprovalFields,
  readNotePushApprovalRows,
} from '../projects/components/ApprovalRequestBlock.display';
import { MergePatientProfilesApprovalDetails } from '../projects/components/MergePatientProfilesApprovalDetails';
import { MergeSessionsApprovalDetails } from '../projects/components/MergeSessionsApprovalDetails';
import { NotePushApprovalDetails } from '../projects/components/NotePushApprovalDetails';
import {
  DELETE_PATIENT_PROFILES_TOOL_NAME,
  readDeletePatientProfilesApprovalFields,
} from './delete-patient-profiles-approval';
import { DeletePatientProfilesApprovalDetails } from './delete-patient-profiles-approval-details';
import { LabelledContextPanel } from './labelled-context-panel';

export interface ApprovalInputDetailsProps {
  approval: ApprovalRequestInput;
}

/**
 * The two rows that read other domains' data, rendered by the app: a merge row's
 * session, and a delete row's patient and linked sessions.
 */
export interface ApprovalInputDetailsHost {
  renderMergeSessionRow: (sessionId: string) => ReactNode;
  /** Keyed by the patient profile id. */
  renderDeletePatientRow: (
    patientProfileId: string,
    deleteLinkedSessions: boolean | undefined
  ) => ReactNode;
}

type DetailsProps = ApprovalInputDetailsProps & ApprovalInputDetailsHost;

// `tool_input` is typed non-optional, but a malformed payload omitting it would throw.
const MergeApprovalInputDetails = ({ approval }: DetailsProps) => {
  const fields = useMemo(
    () => readMergeApprovalFields(approval.tool_input ?? {}),
    [approval.tool_input]
  );
  return <MergePatientProfilesApprovalDetails fields={fields} />;
};

const MergeSessionsApprovalInputDetails = ({
  approval,
  renderMergeSessionRow,
}: DetailsProps) => {
  const fields = useMemo(
    () => readMergeSessionsApprovalFields(approval.tool_input ?? {}),
    [approval.tool_input]
  );
  return (
    <MergeSessionsApprovalDetails fields={fields} renderSession={renderMergeSessionRow} />
  );
};

const DeletePatientProfilesApprovalInputDetails = ({
  approval,
  renderDeletePatientRow,
}: DetailsProps) => {
  const fields = useMemo(
    () => readDeletePatientProfilesApprovalFields(approval.tool_input ?? {}),
    [approval.tool_input]
  );
  return (
    <DeletePatientProfilesApprovalDetails
      fields={fields}
      renderPatient={(id) => renderDeletePatientRow(id, fields.deleteLinkedSessions)}
    />
  );
};

const ApprovalSummaryPanel = ({ summary }: { summary: string }) => {
  const intl = useIntl();
  return (
    <LabelledContextPanel
      label={intl.formatMessage({
        defaultMessage: 'What this does',
        id: 'fSetbgXpkg',
        description: 'Inline approval card: label above the plain-English action summary',
      })}
    >
      <p
        className="text-sm leading-snug text-text-secondary [overflow-wrap:anywhere]"
        data-testid="approval-input-summary"
      >
        {summary}
      </p>
    </LabelledContextPanel>
  );
};

const MultiplayerApprovalInputDetails = ({ approval }: DetailsProps) => {
  const intl = useIntl();
  const preview = useMemo(
    () =>
      readMultiplayerApprovalPreview(intl, approval.tool_name, approval.tool_input ?? {}),
    [approval.tool_name, approval.tool_input, intl]
  );
  return preview ? <ApprovalSummaryPanel summary={preview} /> : null;
};

/**
 * Per-tool opt-in for an approval card's body. A tool the registry does not name
 * never gets a dump of its input; it shows only its bounded display `detail`
 * (for example the patient and session a note push targets), or nothing.
 */
const APPROVAL_INPUT_DETAILS: Record<string, ComponentType<DetailsProps>> = {
  [MERGE_PATIENT_PROFILES_TOOL_NAME]: MergeApprovalInputDetails,
  [MERGE_SESSIONS_TOOL_NAME]: MergeSessionsApprovalInputDetails,
  [DELETE_PATIENT_PROFILES_TOOL_NAME]: DeletePatientProfilesApprovalInputDetails,
  ...Object.fromEntries(
    MULTIPLAYER_APPROVAL_TOOL_NAMES.map((name) => [name, MultiplayerApprovalInputDetails])
  ),
};

export const ApprovalInputDetails = ({
  approval,
  renderMergeSessionRow,
  renderDeletePatientRow,
}: DetailsProps): ReactNode => {
  // Outranks the registry: the server-authored write plan is what actually executes.
  const notePushRows = useMemo(
    () => readNotePushApprovalRows(approval.context),
    [approval.context]
  );
  if (notePushRows) return <NotePushApprovalDetails rows={notePushRows} />;

  const Details = APPROVAL_INPUT_DETAILS[approval.tool_name];
  if (Details) {
    return (
      <Details
        approval={approval}
        renderMergeSessionRow={renderMergeSessionRow}
        renderDeletePatientRow={renderDeletePatientRow}
      />
    );
  }
  const detail = approval.toolCallDisplay?.detail?.trim();
  return detail ? <ApprovalSummaryPanel summary={detail} /> : null;
};
